// ─── Acomodar una pista alrededor de un clip que cambió de tamaño ──────────
// Cuando un clip crece —hoy: "Restaurar", que le devuelve su duración
// original— los vecinos de su pista le quedan encima. Esto los corre para que
// queden PEGADOS a él, sin encimarse y sin cambiar el orden: los de la derecha
// se empujan hacia adelante y los de la izquierda hacia atrás.
//
// Es matemática pura y vive afuera del componente para poder testearla: los
// casos que importan (que no quede nada en negativo, que los huecos entre los
// vecinos se respeten) son fáciles de romper y difíciles de ver a ojo.
// Ver ADR-2026-09-16-restaurar-corre-a-los-vecinos.

export type ClipEnLaPista = {
  id: string;
  startTime: number;
  duration: number;
};

/** Devuelve los `startTime` nuevos (sólo de los que se mueven), incluido el del
 *  clip que cambió: puede terminar corrido si al empujar hacia atrás la pista
 *  se choca con el 0. */
export function acomodarPistaAlrededorDe(params: {
  /** Todos los clips de la pista, incluido el que cambia. */
  clips: ClipEnLaPista[];
  /** El que cambió de tamaño. */
  id: string;
  /** Dónde y cuánto quiere ocupar ahora. */
  nuevoInicio: number;
  nuevaDuracion: number;
}): Map<string, number> {
  const { clips, id, nuevaDuracion } = params;
  const fijo = clips.find((c) => c.id === id);
  if (!fijo) return new Map();

  // El orden de la pista lo define dónde está cada uno HOY: al crecer, el clip
  // no se adelanta ni se atrasa respecto de sus vecinos.
  const otros = clips
    .filter((c) => c.id !== id)
    .sort((a, b) => a.startTime - b.startTime);
  const izquierda = otros.filter((c) => c.startTime < fijo.startTime).reverse();
  const derecha = otros.filter((c) => c.startTime >= fijo.startTime);

  const inicios = new Map<string, number>();
  const inicioFijo = params.nuevoInicio;
  inicios.set(id, inicioFijo);

  // Hacia adelante: cada uno arranca, como mínimo, donde termina el anterior.
  // El empujón viaja como un DESPLAZAMIENTO (`corrimiento`), no pegando cada
  // clip al anterior: así los huecos que el usuario dejó a propósito entre sus
  // clips se mantienen, y el que ya estaba lejos no se mueve.
  let cursor = inicioFijo + nuevaDuracion;
  let corrimiento = 0;
  for (const c of derecha) {
    const inicio = Math.max(c.startTime + corrimiento, cursor);
    corrimiento = inicio - c.startTime;
    inicios.set(c.id, inicio);
    cursor = inicio + c.duration;
  }

  // Hacia atrás, el espejo: cada uno termina, como mucho, donde arranca el
  // siguiente, y el corrimiento (negativo) también viaja en cadena.
  cursor = inicioFijo;
  corrimiento = 0;
  for (const c of izquierda) {
    const inicio = Math.min(c.startTime + corrimiento, cursor - c.duration);
    corrimiento = inicio - c.startTime;
    inicios.set(c.id, inicio);
    cursor = inicio;
  }

  // Si empujando hacia atrás algo se fue antes del 0, no hay dónde ponerlo:
  // se corre TODO el bloque hacia adelante lo que falte. Mover todo (y no sólo
  // lo que quedó negativo) conserva las distancias que se acaban de calcular.
  let masNegativo = 0;
  for (const inicio of inicios.values()) {
    if (inicio < masNegativo) masNegativo = inicio;
  }
  if (masNegativo < 0) {
    for (const [clipId, inicio] of inicios) {
      inicios.set(clipId, inicio - masNegativo);
    }
  }

  // Sólo devolvemos lo que de verdad cambia.
  const cambios = new Map<string, number>();
  for (const [clipId, inicio] of inicios) {
    const actual =
      clipId === id ? fijo.startTime : otros.find((c) => c.id === clipId)!.startTime;
    if (Math.abs(actual - inicio) > 0.0001) cambios.set(clipId, inicio);
  }
  return cambios;
}
